import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirm, setConfirm] = useState({ isOpen: false, title: "", message: "", resolve: null, type: "danger", confirmText: "Confirm", cancelText: "Cancel" });

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const confirmDialog = useCallback(({ title, message, confirmLabel, cancelLabel, type }) => {
    return new Promise((resolve) => {
      setConfirm({
        isOpen: true,
        title,
        message,
        confirmText: confirmLabel || "Confirm",
        cancelText: cancelLabel || "Cancel",
        type: type || "danger",
        resolve
      });
    });
  }, []);

  const handleConfirm = () => {
    confirm.resolve(true);
    setConfirm(prev => ({ ...prev, isOpen: false }));
  };

  const handleCancel = () => {
    confirm.resolve(false);
    setConfirm(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const handleError = (e) => showToast(e.detail, "error");
    const handleSuccess = (e) => showToast(e.detail, "success");
    window.addEventListener("foody-error", handleError);
    window.addEventListener("foody-success", handleSuccess);
    return () => {
      window.removeEventListener("foody-error", handleError);
      window.removeEventListener("foody-success", handleSuccess);
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, confirmDialog }}>
      {children}
      
      {/* Toasts container */}
      <div style={{ 
        position: "fixed", 
        bottom: "calc(100px + env(safe-area-inset-bottom))", 
        left: 0, 
        width: "100%",
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        zIndex: 10000, 
        pointerEvents: "none"
      }}>
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
              style={{
                background: t.type === "error" ? "#1A1A1A" : "#D94F00",
                color: "white",
                padding: "12px 20px",
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 700,
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                pointerEvents: "auto",
                border: "1px solid rgba(255,255,255,0.1)",
                textAlign: "center",
                maxWidth: "90vw",
                margin: "0 auto"
              }}
            >
              {t.type === "success" && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              )}
              {t.type === "error" && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF4D4D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              )}
              {t.type === "favorite" && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
              )}
              {t.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Global Confirm Dialog */}
      <ConfirmDialog 
        isOpen={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
        cancelText={confirm.cancelText}
        type={confirm.type}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

export const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", type = "danger" }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            style={{
              position: "relative",
              background: "white",
              width: "100%",
              maxWidth: 340,
              borderRadius: 24,
              padding: 24,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              textAlign: "center",
              zIndex: 1
            }}
          >
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 8, color: "#1A1A1A" }}>{title}</div>
            <div style={{ fontSize: 14, color: "#666", marginBottom: 24, lineHeight: 1.5 }}>{message}</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button 
                onClick={onCancel}
                style={{ flex: 1, padding: "14px", borderRadius: 14, border: "1px solid #EEE", background: "#F8F9FA", color: "#666", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >
                {cancelText}
              </button>
              <button 
                onClick={onConfirm}
                style={{ flex: 1, padding: "14px", borderRadius: 14, border: "none", background: type === "danger" ? "#DC2626" : "#D94F00", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
